import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Grid } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useHistory, useRouteMatch } from 'react-router-dom';
import SectionCard from '../../../components/SectionCard';
import InvButton from '../../../components/InvButton';
import StatCard from '../../../components/StatCard';
import { InvTextField, InvSelect } from '../../../components/InvField';
import useIpdPrescriptions from '../../../hooks/useIpdPrescriptions';
import useInvRole from '../../../hooks/useInvRole';
import { INV_ACTIONS } from '../../../utils/inventoryRoles';
import {
  PRESCRIPTION_STATUS_FILTER_OPTIONS,
  isDispensablePrescriptionStatus,
} from '../../../utils/prescriptionStatus';
import { fadeInSx } from '../pharmacyStyles';
import { PharmacyFilterBar } from '../PharmacyUi';
import PharmacyTableState from '../PharmacyTableState';
import PrescriptionViewDialog from '../PrescriptionViewDialog';
import IpdPrescriptionsTable from '../components/IpdPrescriptionsTable';
import TablePaginationBar from '../components/TablePaginationBar';

/** Fields stack full-width on phones; fixed widths only once there is room for them. */
const FILTER_FIELD_SX = { flex: { xs: '1 1 100%', sm: '1 1 240px' }, minWidth: { sm: 200 } };
const FILTER_SELECT_SX = { flex: { xs: '1 1 100%', sm: '0 0 220px' }, minWidth: { sm: 200 } };

export default function ActiveDispensesTab() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const base = url.replace('/active-dispenses', '');
  const [selected, setSelected] = useState(null);
  const [viewingRx, setViewingRx] = useState(null);

  const {
    rows,
    filtered,
    pageRows,
    counts,
    openDispenseByOrder,
    controlledDataAvailable,
    isDemoData,
    loading,
    error,
    reload,
    search,
    status,
    flag,
    page,
    rowsPerPage,
    isFiltered,
    setSearch,
    setStatus,
    setKpi,
    setPage,
    setRowsPerPage,
    resetFilters,
  } = useIpdPrescriptions();

  const { can } = useInvRole();
  const canStart = can(INV_ACTIONS.DISPENSE_START);

  const startDispense = useCallback((rx) => {
    if (!canStart || !isDispensablePrescriptionStatus(rx?.status)) return;
    history.push(`${base}/start-dispense/${encodeURIComponent(rx.id)}`);
  }, [base, canStart, history]);

  const openDispense = useCallback((transactionId) => {
    if (!transactionId) return;
    history.push(`${base}/dispenses/${encodeURIComponent(transactionId)}`);
  }, [base, history]);

  /**
   * Counts describe the whole order book, never the current view — that is why they
   * read from `counts` (derived from the full list) and not from `filtered`.
   * Cancelled orders are excluded from the status buckets; they are work nobody has
   * to do. `total` is the full list, including cancelled.
   */
  const cards = useMemo(() => {
    const list = [
      { key: 'total', label: 'Total', value: counts.total, icon: AssignmentOutlinedIcon, tone: 'teal', status: '', flag: null },
      { key: 'pending', label: 'Sent', value: counts.pending, icon: HourglassEmptyIcon, tone: 'amber', status: 'SENT', flag: null },
      { key: 'completed', label: 'Completed', value: counts.completed, icon: CheckCircleOutlineIcon, tone: 'mint', status: 'COMPLETED', flag: null },
      { key: 'alerts', label: 'With Alerts', value: counts.alerts, icon: WarningAmberIcon, tone: 'red', status: '', flag: 'alerts' },
    ];
    // Only when the API actually sends controlled-drug fields. Absent data must not
    // be reported as a zero — see hasControlledDataSignal.
    if (controlledDataAvailable) {
      list.push({
        key: 'controlled',
        label: 'With Controlled',
        value: counts.controlled,
        icon: LocalPharmacyIcon,
        tone: 'amber',
        status: '',
        flag: 'controlled',
      });
    }
    return list;
  }, [counts, controlledDataAvailable]);

  const selectKpi = useCallback((card) => {
    setKpi(card.status, card.flag);
  }, [setKpi]);

  const isKpiSelected = useCallback((card) => {
    if (card.flag) return flag === card.flag;
    if (flag) return false;
    return status === card.status;
  }, [flag, status]);

  const cardSpan = cards.length > 4 ? 4 : 3;
  const isEmpty = !loading && !error && filtered.length === 0;

  return (
    <Box sx={fadeInSx}>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {cards.map((card) => (
          <Grid item xs={6} sm={cardSpan} key={card.key}>
            <StatCard
              label={card.label}
              value={card.value}
              icon={card.icon}
              tone={card.tone}
              loading={loading}
              selected={isKpiSelected(card)}
              onClick={() => selectKpi(card)}
            />
          </Grid>
        ))}
      </Grid>

      {isDemoData && (
        <Alert severity="info" sx={{ borderRadius: '10px', mb: 2 }}>
          Sample data — no IPD prescriptions were returned for this facility.
        </Alert>
      )}

      <PharmacyFilterBar>
        <Box sx={FILTER_FIELD_SX}>
          <InvTextField
            label="Search prescriptions"
            placeholder="Order no, UHID, patient, doctor, room or bed"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Box sx={FILTER_SELECT_SX}>
          <InvSelect
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={PRESCRIPTION_STATUS_FILTER_OPTIONS}
          />
        </Box>
      </PharmacyFilterBar>

      <SectionCard
        title="IPD Prescriptions"
        subtitle={`Showing ${filtered.length} of ${rows.length} prescription(s)`}
      >
        <PharmacyTableState
          loading={loading}
          error={error}
          empty={isEmpty}
          emptyIcon={isFiltered ? SearchOffIcon : LocalPharmacyIcon}
          emptyTitle={isFiltered ? 'No matching prescriptions' : 'No IPD prescriptions'}
          emptyMessage={isFiltered
            ? 'No prescriptions match your search and status filter.'
            : 'Prescriptions appear here once a doctor sends an order to the pharmacy.'}
          emptyAction={isFiltered && (
            <InvButton variant="outlined" tone="teal" size="small" onClick={resetFilters}>
              Clear filters
            </InvButton>
          )}
          onRetry={reload}
        >
          <IpdPrescriptionsTable
            rows={pageRows}
            selectedId={selected?.id}
            canStart={canStart}
            onSelect={setSelected}
            onView={setViewingRx}
            onStartDispense={startDispense}
            openDispenseByOrder={openDispenseByOrder}
            onOpenDispense={openDispense}
          />
        </PharmacyTableState>

        {/* Outside PharmacyTableState: it returns `children` bare, so a second child
            would become an unkeyed array — and a pager has nothing to page through
            during the loading, error and empty states anyway. */}
        {!loading && !error && filtered.length > 0 && (
          <TablePaginationBar
            page={page}
            rowsPerPage={rowsPerPage}
            total={filtered.length}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        )}
      </SectionCard>

      <PrescriptionViewDialog
        open={!!viewingRx}
        prescription={viewingRx}
        onClose={() => setViewingRx(null)}
        onStartDispense={canStart ? startDispense : undefined}
      />
    </Box>
  );
}
